import { ReactNode, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Logo } from '@/components/ui/logo';
import { IS_HOOFDNAARHART, LOGIN_PATH, MARKETING_URL } from '@/lib/constants';

/** What the apps call themselves when addressing a visitor. */
export const APP_NAME = IS_HOOFDNAARHART
  ? 'Van Hoofd naar Hart'
  : 'Van Hoofdzorg naar Hartzorg';

interface PublicPageProps {
  title: string;
  /** Fills the meta description; keep it a sentence, it is what a search result shows. */
  description: string;
  subtitle?: string;
  children: ReactNode;
}

/**
 * The frame for the pages a visitor can reach without signing in.
 *
 * These exist because both app stores want a reachable privacy policy and a
 * support address in the listing, and until now those only lived inside the
 * login page's dialogs -- no URL to hand over. They double as the pages a
 * reviewer or a curious visitor lands on.
 *
 * The title and description are set per page rather than in index.html, which
 * carries one title for the whole SPA.
 */
export function PublicPage({
  title,
  description,
  subtitle,
  children,
}: PublicPageProps) {
  useEffect(() => {
    const previous = document.title;
    document.title = `${title} · ${APP_NAME}`;

    let meta = document.querySelector<HTMLMetaElement>('meta[name="description"]');
    const added = !meta;
    if (!meta) {
      meta = document.createElement('meta');
      meta.name = 'description';
      document.head.appendChild(meta);
    }
    const previousDescription = meta.content;
    meta.content = description;

    return () => {
      document.title = previous;
      if (added) meta?.remove();
      else if (meta) meta.content = previousDescription;
    };
  }, [title, description]);

  return (
    <div className="min-h-screen bg-muted/40">
      <header className="border-b bg-background">
        <div className="mx-auto flex max-w-3xl items-center justify-between gap-4 px-4 py-4">
          <Logo className="h-10 w-auto" linkTo={LOGIN_PATH} />
          <Link
            to={LOGIN_PATH}
            className="text-sm font-medium text-green-700 underline underline-offset-4 hover:text-green-800"
          >
            Inloggen
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-4 py-8">
        <h1 className="text-2xl font-semibold">{title}</h1>
        {subtitle && (
          <p className="mt-1 text-sm text-muted-foreground">{subtitle}</p>
        )}
        <div className="mt-6 rounded-lg border bg-card p-6">{children}</div>
      </main>

      <footer className="mx-auto max-w-3xl px-4 pb-10 text-sm text-muted-foreground">
        <nav className="flex flex-wrap gap-x-4 gap-y-2">
          <Link to="/privacy" className="underline underline-offset-4 hover:text-foreground">
            Privacy
          </Link>
          <Link to="/voorwaarden" className="underline underline-offset-4 hover:text-foreground">
            Voorwaarden
          </Link>
          <Link to="/support" className="underline underline-offset-4 hover:text-foreground">
            Support
          </Link>
          <Link
            to="/account-verwijderen"
            className="underline underline-offset-4 hover:text-foreground"
          >
            Account verwijderen
          </Link>
          {/* New tab on purpose: inside the native webview there is no back
              button, so navigating away would strand the visitor. */}
          <a
            href={MARKETING_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="underline underline-offset-4 hover:text-foreground"
          >
            Over de bijeenkomsten
          </a>
        </nav>
        <p className="mt-4">{APP_NAME}</p>
      </footer>
    </div>
  );
}
